import type { LucideIcon } from 'lucide-react';
import { createContext, useContext, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const InStripContext = createContext(false);

/**
 * Faixa de indicadores: um único painel com as métricas separadas por divisórias discretas.
 * Duas colunas no mobile; em telas maiores as métricas dividem a largura (quebrando linha
 * quando não cabem) e a última linha sempre se estende até o fim, sem buracos.
 */
export function MetricStrip({
  children,
  className,
  title,
  description,
}: {
  children: ReactNode;
  className?: string;
  /** Rótulo do grupo (ex.: "No período", "Posição atual"). */
  title?: string;
  description?: string;
}) {
  return (
    <section className={cn('space-y-2', className)} aria-label={title}>
      {title ? (
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <h2 className="text-[13px] font-semibold text-foreground">{title}</h2>
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>
      ) : null}
      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="-mt-px -ml-px flex flex-wrap">
          <InStripContext.Provider value={true}>{children}</InStripContext.Provider>
        </div>
      </div>
    </section>
  );
}

interface StatCardProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
  loading?: boolean;
  /** Cor do valor: negativo (vermelho), alerta (âmbar), positivo (verde). */
  tone?: 'default' | 'positive' | 'negative' | 'warning';
  /** Marca curta ao lado do rótulo, p.ex. "período" ou "atual". */
  tag?: string;
  /** Torna a métrica um atalho para a tela de detalhe. */
  to?: string;
  className?: string;
}

const TONES = {
  default: 'text-foreground',
  positive: 'text-success',
  negative: 'text-destructive',
  warning: 'text-warning',
} as const;

/**
 * Métrica. Dentro de `MetricStrip` é uma célula da faixa; fora dela, um painel próprio.
 */
export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  loading,
  tone = 'default',
  tag,
  to,
  className,
}: StatCardProps) {
  const inStrip = useContext(InStripContext);
  const body = (
    <>
      <p className="flex min-w-0 items-start gap-1.5 text-xs font-medium text-muted-foreground">
        {Icon ? <Icon className="size-3.5 shrink-0" aria-hidden /> : null}
        <span className="line-clamp-2 min-w-0 leading-tight">{label}</span>
        {tag ? (
          <span className="ml-auto shrink-0 rounded-sm bg-muted px-1.5 py-px text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
            {tag}
          </span>
        ) : null}
      </p>
      {loading ? (
        <Skeleton className="mt-1.5 h-6 w-24" />
      ) : (
        <p
          className={cn(
            'mt-1 truncate text-lg leading-7 font-semibold tracking-tight tabular-nums sm:text-xl',
            TONES[tone],
          )}
        >
          {value ?? '—'}
        </p>
      )}
      {hint ? (
        <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground">{hint}</p>
      ) : null}
    </>
  );
  const cellClass = cn(
    'block min-w-0 px-4 py-3.5',
    inStrip
      ? 'grow basis-[calc(50%-1px)] border-t border-l md:basis-44'
      : 'rounded-xl border bg-card',
    to && 'transition-colors hover:bg-subtle focus-visible:bg-subtle focus-visible:outline-none',
    className,
  );
  return to ? (
    <Link to={to} className={cellClass}>
      {body}
    </Link>
  ) : (
    <div className={cellClass}>{body}</div>
  );
}
